import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";

const API = `${window.location.origin}/api`;

const GOLD = "#C4A671";

const reveal = (delay = 0) => ({
  initial: { opacity: 0, y: 34 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.9, delay, ease: [0.22, 1, 0.36, 1] },
});

function CollageImage({ src, alt, className, delay, testId }) {
  return (
    <motion.div
      {...reveal(delay)}
      className={`absolute overflow-hidden border ${className}`}
      style={{ borderColor: "rgba(196,166,113,0.45)", boxShadow: "0 30px 60px rgba(0,0,0,0.55)" }}
      data-testid={testId}
    >
      <img
        src={src}
        alt={alt}
        loading="lazy"
        className="w-full h-full object-cover transition-transform duration-[1.6s] hover:scale-[1.06]"
      />
    </motion.div>
  );
}

export default function RegistryShowcase() {
  const navigate = useNavigate();
  const [paesi, setPaesi] = useState(null);

  useEffect(() => {
    axios.get(`${API}/registry/stats`).then(({ data }) => setPaesi(data?.paesi)).catch(() => {});
  }, []);

  return (
    <section
      id="registro"
      data-testid="registry-showcase-section"
      className="relative bg-[#0A0A0A] text-[#F2EFE8] overflow-hidden"
    >
      <div className="absolute inset-0 grain opacity-25 pointer-events-none" aria-hidden />
      <div className="hairline" style={{ background: "rgba(196,166,113,0.18)" }} />

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-24 sm:py-32 lg:py-40">
        <div className="grid lg:grid-cols-2 gap-14 lg:gap-20 items-center">
          {/* Testo */}
          <div>
            <motion.div {...reveal(0)} className="flex items-center gap-5 mb-7">
              <span className="section-num text-3xl" style={{ color: GOLD }}>11</span>
              <div className="h-px w-14" style={{ background: GOLD }} />
              <span className="overline text-[10px]" style={{ color: GOLD }}>
                Il Registro Globale
              </span>
            </motion.div>

            <motion.h2
              {...reveal(0.08)}
              className="font-serif-display leading-[0.95]"
              style={{ fontSize: "clamp(2.4rem, 4.4vw, 4.2rem)" }}
            >
              Allocazioni{" "}
              <span className="italic font-light" style={{ color: GOLD }}>d&apos;Autore.</span>
            </motion.h2>

            <motion.p
              {...reveal(0.16)}
              className="mt-7 text-[15px] sm:text-base leading-[1.9] font-light max-w-xl"
              style={{ color: "#C9C3B5" }}
            >
              Le creazioni dei grandi chef e sommelier del mondo, nate attorno alle
              nostre allocazioni, iscritte al Registro della Maison con la propria
              Matrice delle Affinità Sensoriali. Una mappa mondiale che racconta
              dove i nostri vini diventano opere d&apos;autore.
            </motion.p>

            {paesi != null && (
              <motion.div
                {...reveal(0.24)}
                className="mt-9 flex items-baseline gap-3"
                data-testid="registry-showcase-counter"
              >
                <span className="font-serif-display text-[44px] leading-none" style={{ color: GOLD }}>
                  {paesi}
                </span>
                <span className="text-[12.5px] font-light max-w-[300px] leading-snug" style={{ color: "#8B7A55" }}>
                  Paesi — consultato da professionisti e buyer d&apos;élite
                </span>
              </motion.div>
            )}

            <motion.div {...reveal(0.32)} className="mt-10">
              <button
                data-testid="registry-showcase-cta"
                onClick={() => navigate("/allocazioni")}
                className="group inline-flex items-center gap-4 px-9 py-4 text-[10.5px] tracking-[0.26em] uppercase transition-all duration-300 hover:gap-6"
                style={{ background: GOLD, color: "#0A0A0A" }}
              >
                Esplori il Registro
                <span aria-hidden>→</span>
              </button>
            </motion.div>
          </div>

          {/* Collage */}
          <div data-testid="registry-showcase-collage">
            <div className="relative h-[380px] sm:h-[460px] lg:h-[540px]">
              <CollageImage
                src="/registry/parigi.webp"
                alt="Creazione d'autore — Parigi"
                className="right-0 top-0 w-[64%] aspect-[4/3]"
                delay={0.1}
                testId="collage-parigi"
              />
              <CollageImage
                src="/registry/tokyo.webp"
                alt="Creazione d'autore — Tokyo"
                className="left-0 top-[34%] w-[46%] aspect-square z-10"
                delay={0.25}
                testId="collage-tokyo"
              />
              <CollageImage
                src="/registry/newyork.webp"
                alt="Creazione d'autore — New York"
                className="right-[6%] bottom-0 w-[40%] aspect-[4/3] z-20"
                delay={0.4}
                testId="collage-newyork"
              />
            </div>
            <motion.div
              {...reveal(0.55)}
              className="mt-5 inline-flex flex-col border px-4 py-3"
              style={{ background: "rgba(10,10,10,0.92)", borderColor: "rgba(196,166,113,0.5)" }}
              data-testid="collage-matrix-chip"
            >
              <span className="overline text-[8.5px] block" style={{ color: "#8B7A55" }}>
                Matrice delle Affinità Sensoriali
              </span>
              <span className="font-serif-display text-[20px]" style={{ color: GOLD }}>
                9.2<span className="text-[12px]" style={{ color: "#7A7568" }}> / 10</span>
              </span>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}
